import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import { useInView } from "react-intersection-observer"
import { useRouter } from "next/router"

import { DEFAULT_PAGE_SIZE } from "@/constants/pagination"
import { AddToHumanReviewButton } from "@/human-feedback/projects"
import { useBaselineDeviationsBySimulation } from "@/metrics/baselines"
import { MetricModal } from "@/metrics/configure"
import { Skeleton } from "@coval/ui"
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react"
import type { SortDescriptor as TableSortDescriptor } from "@react-types/shared"
import { Copy, Plus, X } from "lucide-react"

import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"
import { cn } from "@/utils/cn"
import type { DrillDownColumnConfig } from "@/utils/csv/csv-export"

import { useDrillDownAdditionalMetrics } from "../hooks/useDrillDownAdditionalMetrics"
import {
  useUnifiedMetricDrillDownRows,
  type UseUnifiedMetricDrillDownRowsResult,
} from "../hooks/useUnifiedMetricDrillDownRows"
import {
  buildDrillDownConversationPath,
  getDrillDownConversationLabel,
} from "../utils/drillDownNavigation"
import type { ChartClickData } from "../widgets/types/chart"
import type { DrillDownRunRecord } from "../widgets/types/core"
import { DrillDownCSVExportDropdown } from "./DrillDownCSVExportDropdown"

const log = logger("UnifiedMetricDrillDownPanel")

interface UnifiedMetricDrillDownPanelProps {
  clickData: ChartClickData
  baselineId?: string
  drillDownRowsState?: UseUnifiedMetricDrillDownRowsResult
  focusedSimulationOutputId?: string | null
  highlightedSimulationOutputId?: string | null
  onClose: () => void
}

interface SortDescriptor {
  column: keyof DrillDownRunRecord | "deviationSigma"
  direction: "ascending" | "descending"
}

interface ColumnConfig extends DrillDownColumnConfig {
  sortable: boolean
  align: "start" | "center" | "end"
}

export const UnifiedMetricDrillDownPanel: React.FC<
  UnifiedMetricDrillDownPanelProps
> = ({
  clickData,
  baselineId,
  drillDownRowsState,
  focusedSimulationOutputId,
  highlightedSimulationOutputId,
}) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const slug = typeof router.query.slug === "string" ? router.query.slug : ""
  const instanceId = useId()
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "occurred_at",
    direction: "descending",
  })

  const [currentPage, setCurrentPage] = useState(1)

  const internalDrillDownRowsState = useUnifiedMetricDrillDownRows({
    clickData,
    enabled: !drillDownRowsState,
  })
  const rowsState = drillDownRowsState ?? internalDrillDownRowsState
  const {
    error,
    isLoading,
    rows,
    simulationOutputIds,
    timezone,
    uniqueSimulationOutputIds,
  } = rowsState

  const {
    additionalMetricIds,
    additionalValuesMap,
    handleApplyMetrics,
    handleOpenMetricModal,
    handleRemoveMetric,
    handleToggleMetric,
    isAdditionalMetricValueLoading,
    isMetricModalOpen,
    metricDisplayNames,
    metricRecords,
    setIsMetricModalOpen,
    tempSelectedMetrics,
  } = useDrillDownAdditionalMetrics({ simulationOutputIds })

  // Fetch baseline deviations for the visible simulations
  const { data: baselineDeviations } = useBaselineDeviationsBySimulation(
    {
      baselineId: baselineId ?? "",
      simulationOutputIds: uniqueSimulationOutputIds,
    },
    {
      enabled: !!baselineId && uniqueSimulationOutputIds.length > 0,
    },
  )

  const deviationMap = useMemo(() => {
    const map = new Map<string, number>()
    if (!baselineDeviations) {
      return map
    }
    for (const d of baselineDeviations) {
      map.set(d.simulation_output_id, d.deviation_sigma)
    }
    return map
  }, [baselineDeviations])

  const formatTimestamp = useCallback(
    (timestamp: string) => {
      try {
        const date = new Date(timestamp)
        return date.toLocaleString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: timezone,
        })
      } catch (error) {
        log.error("Error formatting timestamp", error, { timestamp })
        return timestamp
      }
    },
    [timezone],
  )

  const sortedRows = useMemo(() => {
    if (!rows) {
      return []
    }

    return [...rows].sort((a: DrillDownRunRecord, b: DrillDownRunRecord) => {
      let aValue, bValue

      if (sortDescriptor.column === "occurred_at") {
        aValue = new Date(a.occurred_at).getTime()
        bValue = new Date(b.occurred_at).getTime()
      } else if (sortDescriptor.column === "created_at") {
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0
      } else if (sortDescriptor.column === "value") {
        aValue = a.value
        bValue = b.value
      } else if (sortDescriptor.column === "deviationSigma") {
        aValue = deviationMap.get(a.simulation_output_id) ?? 0
        bValue = deviationMap.get(b.simulation_output_id) ?? 0
      } else {
        aValue = a[sortDescriptor.column] ?? ""
        bValue = b[sortDescriptor.column] ?? ""
      }

      if (sortDescriptor.direction === "descending") {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    })
  }, [rows, sortDescriptor, deviationMap])

  // Reset page when the underlying data or sort changes
  useEffect(() => {
    setCurrentPage(1)
  }, [rows, sortDescriptor])

  useEffect(() => {
    if (!focusedSimulationOutputId) {
      return
    }

    const focusedIndex = sortedRows.findIndex(
      (row) => row.simulation_output_id === focusedSimulationOutputId,
    )
    if (focusedIndex === -1) {
      return
    }

    setCurrentPage((page) =>
      Math.max(page, Math.ceil((focusedIndex + 1) / DEFAULT_PAGE_SIZE)),
    )
  }, [focusedSimulationOutputId, sortedRows])

  // Derive displayed rows from sortedRows + currentPage (no stale-state race)
  const displayedRows = useMemo(
    () => sortedRows.slice(0, currentPage * DEFAULT_PAGE_SIZE),
    [sortedRows, currentPage],
  )

  const hasMore = displayedRows.length < sortedRows.length
  const highlightedRowId =
    highlightedSimulationOutputId ?? focusedSimulationOutputId

  useEffect(() => {
    if (!focusedSimulationOutputId) {
      return
    }

    const focusedRow = Array.from(
      tableContainerRef.current?.querySelectorAll<HTMLElement>(
        "[data-simulation-output-id]",
      ) ?? [],
    ).find(
      (row) => row.dataset.simulationOutputId === focusedSimulationOutputId,
    )

    focusedRow?.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [displayedRows, focusedSimulationOutputId])

  // Infinite scroll — sentinel lives inside Table's bottomContent (within the scroll container)
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
  })

  useEffect(() => {
    if (inView && hasMore) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [inView, hasMore])

  const handleSortChange = (descriptor: TableSortDescriptor) => {
    setSortDescriptor(descriptor as SortDescriptor)
  }

  const metricColumnLabel = useMemo(() => {
    const metricName = clickData.displayLabel || "Value"
    return metricName.length > 30
      ? `${metricName.substring(0, 30)}...`
      : metricName
  }, [clickData.displayLabel])

  // Build columns including additional metrics
  const columns: ColumnConfig[] = useMemo(() => {
    const baseColumns: ColumnConfig[] = [
      {
        key: "simulation_output_id",
        label: "ID",
        sortable: false,
        align: "start",
      },
      {
        key: "occurred_at",
        label: "Occurred At",
        sortable: true,
        align: "center",
      },
      {
        key: "created_at",
        label: "Created At",
        sortable: true,
        align: "center",
      },
      {
        key: "value",
        label: metricColumnLabel,
        sortable: true,
        align: "center",
      },
    ]

    if (baselineId && deviationMap.size > 0) {
      baseColumns.push({
        key: "deviationSigma",
        label: "Deviation",
        sortable: true,
        align: "center",
      })
    }

    // Add columns for additional metrics
    const additionalColumns: ColumnConfig[] = additionalMetricIds.map(
      (metricId) => ({
        key: `metric_${metricId}`,
        label: metricDisplayNames.get(metricId) || metricId.slice(0, 8),
        sortable: false,
        align: "center" as const,
        isAdditionalMetric: true,
        metricId,
      }),
    )

    return [...baseColumns, ...additionalColumns]
  }, [
    metricColumnLabel,
    additionalMetricIds,
    metricDisplayNames,
    baselineId,
    deviationMap,
  ])

  const handleCopyId = useCallback((e: React.SyntheticEvent, id: string) => {
    e.stopPropagation()
    void navigator.clipboard.writeText(id)
  }, [])

  const handleCopyIdKeyDown = useCallback(
    (event: React.KeyboardEvent, id: string) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return
      }

      event.preventDefault()
      handleCopyId(event, id)
    },
    [handleCopyId],
  )

  const getConversationHref = useCallback(
    (row: DrillDownRunRecord) =>
      queryParams.getHref(buildDrillDownConversationPath(row), slug),
    [queryParams, slug],
  )

  const handleOpenConversation = useCallback(
    (row: DrillDownRunRecord) => {
      queryParams.push(buildDrillDownConversationPath(row), slug)
    },
    [queryParams, slug],
  )

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent, row: DrillDownRunRecord) => {
      if (event.target !== event.currentTarget) {
        return
      }

      if (event.key !== "Enter" && event.key !== " ") {
        return
      }

      event.preventDefault()
      handleOpenConversation(row)
    },
    [handleOpenConversation],
  )

  const renderSimulationIdCell = (row: DrillDownRunRecord) => (
    <div className="group/id flex items-center gap-1">
      <a
        href={getConversationHref(row)}
        onClick={(e) => {
          e.stopPropagation()
          if (
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey ||
            e.button !== 0
          ) {
            return
          }
          e.preventDefault()
          handleOpenConversation(row)
        }}
        className="relative text-foreground hover:text-coval-text cursor-pointer font-mono text-sm overflow-hidden whitespace-nowrap"
        title={row.simulation_output_id}
        aria-label={getDrillDownConversationLabel(row)}
      >
        <span className="inline-block w-[6ch] overflow-hidden bg-linear-to-r from-foreground to-transparent bg-clip-text text-transparent select-none">
          {row.simulation_output_id.slice(0, 6)}
        </span>
      </a>
      <button
        onClick={(e) => handleCopyId(e, row.simulation_output_id)}
        onKeyDown={(e) => handleCopyIdKeyDown(e, row.simulation_output_id)}
        className="opacity-0 group-hover/id:opacity-100 transition-opacity p-0.5 hover:bg-surface-2 rounded"
        aria-label={`Copy ID ${row.simulation_output_id}`}
      >
        <Copy className="h-3.5 w-3.5 text-coval-text-muted" />
      </button>
    </div>
  )

  const renderCell = (column: ColumnConfig, row: DrillDownRunRecord) => {
    switch (column.key) {
      case "simulation_output_id":
        return renderSimulationIdCell(row)
      case "occurred_at":
        return (
          <div className="text-left">{formatTimestamp(row.occurred_at)}</div>
        )
      case "created_at":
        return (
          <div className="text-left text-coval-text-muted">
            {row.created_at ? formatTimestamp(row.created_at) : "-"}
          </div>
        )
      case "value": {
        // Check if it's a numeric string or number
        const numValue =
          typeof row.value === "string" ? parseFloat(row.value) : row.value
        const displayValue = !isNaN(numValue) ? numValue.toFixed(2) : row.value
        return <div className="text-left">{displayValue}</div>
      }
      case "deviationSigma": {
        const sigma = deviationMap.get(row.simulation_output_id)
        if (sigma === undefined) {
          return <div className="text-left text-coval-text-muted">-</div>
        }
        const absVal = Math.abs(sigma)
        const chipColor =
          absVal >= 2 ? "danger" : absVal >= 1 ? "warning" : "default"
        return (
          <Chip
            size="sm"
            variant="flat"
            color={chipColor}
            className="rounded-sm"
          >
            {sigma >= 0 ? "+" : ""}
            {sigma.toFixed(2)}σ
          </Chip>
        )
      }
      default:
        // Handle additional metric columns
        if (column.isAdditionalMetric && column.metricId) {
          if (isAdditionalMetricValueLoading) {
            return (
              <Skeleton
                aria-label="Loading additional metric value"
                className="h-4 w-12 rounded-md"
              />
            )
          }

          const metricValue = additionalValuesMap
            .get(row.simulation_output_id)
            ?.get(column.metricId)

          if (metricValue === undefined) {
            return <div className="text-left text-coval-text-muted">-</div>
          }

          const numVal =
            typeof metricValue === "string"
              ? parseFloat(metricValue)
              : metricValue
          const display = !isNaN(numVal) ? numVal.toFixed(2) : metricValue
          return <div className="text-left">{display}</div>
        }
        return row[column.key as keyof DrillDownRunRecord]
    }
  }

  const getCellClassName = (_columnKey: string) => {
    return "text-left"
  }

  return (
    <div className="h-full flex flex-col bg-background border-l border-divider">
      <div className="flex-1 flex flex-col min-h-0">
        {error ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-red-500">
              <div className="text-sm font-medium">
                Failed to load run details
              </div>
              <div className="text-xs mt-1 text-coval-text-muted">
                Please try selecting a different data point
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-coval-text-muted">
              <div className="text-sm">Loading run data...</div>
            </div>
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-coval-text-muted">
              <div className="text-sm">No runs found for this data point</div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col w-full flex-1 min-h-0">
            {/* Add Metric and Export Buttons */}
            <div className="flex justify-end gap-2 px-4 py-3 border-b border-divider bg-background z-30 shrink-0">
              <AddToHumanReviewButton
                simulationOutputIds={uniqueSimulationOutputIds}
              />
              <DrillDownCSVExportDropdown
                rows={sortedRows}
                columns={columns}
                formatTimestamp={formatTimestamp}
                additionalValuesMap={additionalValuesMap}
                metricDisplayLabel={clickData.displayLabel}
                slug={slug}
              />
              <Button
                size="sm"
                variant="flat"
                isIconOnly
                onPress={handleOpenMetricModal}
                aria-label="Add metric column"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Metric Selection Modal */}
            <MetricModal
              isOpen={isMetricModalOpen}
              onClose={() => setIsMetricModalOpen(false)}
              onApply={handleApplyMetrics}
              metrics={Object.values(metricRecords || {})}
              selectedMetrics={tempSelectedMetrics}
              onToggleMetric={handleToggleMetric}
              instanceId={instanceId}
              allowMultipleSelection
            />

            <div
              ref={tableContainerRef}
              className="drilldown-table-container flex-1 min-h-0 bg-background"
            >
              <style>{`
                .drilldown-table-container thead tr[aria-hidden="true"] {
                  display: none !important;
                }
                .drilldown-table-container thead tr:first-child th {
                  border-bottom: 1px solid hsl(var(--heroui-default) / 0.2) !important;
                }
                .drilldown-table-container tbody tr:hover td {
                  background: hsl(var(--heroui-default-100)) !important;
                }
                .drilldown-table-container tbody td {
                  vertical-align: middle !important;
                }
              `}</style>
              <Table
                aria-label="Run details table"
                isHeaderSticky
                sortDescriptor={sortDescriptor}
                onSortChange={handleSortChange}
                key={`table-${sortedRows.length}-${columns.length}`}
                bottomContent={
                  hasMore ? (
                    <div ref={loadMoreRef} className="flex justify-center py-4">
                      <Skeleton className="h-4 w-16 rounded-md" />
                    </div>
                  ) : null
                }
                classNames={{
                  base: "h-full overflow-auto",
                  wrapper: "h-full shadow-none p-0",
                  thead: "bg-background z-20",
                  th: "bg-background text-coval-text text-sm font-semibold border-b border-divider text-left py-0 px-3 first:pl-4 last:pr-4 align-middle!",
                  td: "relative text-sm border-b border-divider py-1 px-3 first:pl-4 last:pr-4 align-middle!",
                  tr: "h-12",
                }}
              >
                <TableHeader columns={columns}>
                  {(column) => (
                    <TableColumn
                      key={column.key}
                      allowsSorting={column.sortable}
                      align={column.align}
                    >
                      {column.isAdditionalMetric ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className="truncate max-w-[100px]">
                            {column.label}
                          </span>
                          <button
                            onClick={() => handleRemoveMetric(column.metricId!)}
                            className="p-0.5 hover:bg-surface-2 rounded"
                            aria-label={`Remove ${column.label}`}
                          >
                            <X className="h-3 w-3 text-coval-text-muted" />
                          </button>
                        </div>
                      ) : (
                        column.label
                      )}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody
                  items={displayedRows}
                  emptyContent="No runs found for the selected data point"
                >
                  {(row: DrillDownRunRecord) => {
                    const isHighlighted =
                      highlightedRowId === row.simulation_output_id

                    return (
                      <TableRow
                        key={`${row.simulation_output_id}-${row.metric_id}`}
                        className={cn(
                          "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-coval-contrast",
                          isHighlighted && "bg-blue-50 dark:bg-blue-950/30",
                        )}
                        data-simulation-output-id={row.simulation_output_id}
                        aria-label={getDrillDownConversationLabel(row)}
                        tabIndex={0}
                        onClick={() => handleOpenConversation(row)}
                        onKeyDown={(event) => handleRowKeyDown(event, row)}
                      >
                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            className={cn(
                              getCellClassName(column.key),
                              isHighlighted && "bg-blue-50 dark:bg-blue-950/30",
                            )}
                          >
                            {renderCell(column, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  }}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
